import { Component, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { RouterModule } from '@angular/router';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { AuthService } from '../../../shared/services/apis/usuario/auth.service';
import { ConfigVendasGeralService } from '@shared/services/apis/vendas/config-vendas-geral.service';

interface ConfigVendasMenuItem {
  label: string;
  route: string;
  icon: string;
  // Item visível apenas ao usuário master — a API também recusa quem não é.
  somenteMaster?: boolean;
  // Itens visíveis só quando a empresa trabalha com separação/expedição (Configurações Gerais de Vendas).
  exigeSeparacao?: boolean;
  exigeExpedicao?: boolean;
}

interface ConfigVendasMenuGroup {
  title: string;
  items: ConfigVendasMenuItem[];
}

@Component({
  selector: 'app-config-vendas',
  standalone: true,
  imports: [
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTabsModule,
    HeaderComponent,
    EmptyListComponent,
    MatButtonModule,
    ReactiveFormsModule,
  ],
  templateUrl: './config-vendas.component.html',
  styleUrls: ['./config-vendas.component.css'],
})
export class ConfigVendasComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;

  public menuGroups: ConfigVendasMenuGroup[] = [
    {
      title: 'Geral',
      items: [{ label: 'Gerais', route: 'gerais', icon: 'settings' }],
    },
    {
      title: 'Categorias e tags',
      items: [
        { label: 'Tags de venda', route: 'tags-venda', icon: 'sell' },
        { label: 'Tags de CRM', route: 'tags-crm', icon: 'local_offer' },
        { label: 'Tags de devolução de venda', route: 'tags-devolucao-venda', icon: 'sell' },
        { label: 'Tags de separação', route: 'tags-separacao', icon: 'sell' },
        { label: 'Tags de NF-e de saída', route: 'tags-nfe-saida', icon: 'sell' },
      ],
    },
    {
      title: 'Devoluções',
      items: [{ label: 'Devoluções de vendas', route: 'devolucoes', icon: 'assignment_return' }],
    },
    {
      title: 'Ponto de venda',
      items: [{ label: 'Configurações do PDV', route: 'configuracao-pdv', icon: 'point_of_sale' }],
    },
    {
      title: 'Orçamentos',
      items: [{ label: 'Configurações dos orçamentos', route: 'orcamento', icon: 'description' }],
    },
    {
      title: 'Pedidos de venda',
      items: [{ label: 'Configurações dos pedidos de venda', route: 'pedido-venda', icon: 'receipt_long' }],
    },
    {
      title: 'CRM',
      items: [
        {
          label: 'Configuração do Kanban de Oportunidades',
          route: 'kanban-oportunidades',
          icon: 'view_kanban',
          somenteMaster: true,
        },
      ],
    },
    {
      title: 'Kanban',
      items: [
        { label: 'Etapas do kanban de vendas', route: 'kanban', icon: 'account_tree' },
        { label: 'Motivos de transição', route: 'motivos-transicao', icon: 'swap_horiz' },
      ],
    },
    {
      title: 'Automações',
      items: [{ label: 'Automações de vendas', route: 'automacoes', icon: 'autorenew' }],
    },
    {
      title: 'Logística',
      items: [
        { label: 'Formas de envio', route: 'formas-envio', icon: 'local_shipping' },
        { label: 'Separação', route: 'separacao', icon: 'inventory', exigeSeparacao: true },
        { label: 'Expedição', route: 'expedicao', icon: 'package_2', exigeExpedicao: true },
      ],
    },
  ];

  // menuGroups filtrado por permissão; é a base sobre a qual a busca opera.
  private menuGroupsVisiveis: ConfigVendasMenuGroup[] = [];

  public filteredMenuGroups: ConfigVendasMenuGroup[] = [];
  public pesquisaControl = new FormControl('');

  // Até a configuração geral responder, Separação e Expedição ficam ocultas.
  private isTrabalhaComSeparacao = false;
  private isTrabalhaComExpedicao = false;

  constructor(
    private authService: AuthService,
    private configVendasGeralService: ConfigVendasGeralService
  ) {}

  ngOnInit(): void {
    this.aplicarVisibilidade();
    this.pesquisaControl.valueChanges.subscribe(() => {
      this.filtrar();
    });
    this.filtrar();
    this.carregarLogistica();
  }

  private carregarLogistica(): void {
    this.configVendasGeralService.getLookup().subscribe({
      next: res => {
        if (res.isError || !res.configVendasGeral) return;
        this.isTrabalhaComSeparacao = res.configVendasGeral.isTrabalhaComSeparacao;
        this.isTrabalhaComExpedicao = res.configVendasGeral.isTrabalhaComExpedicao;
        this.aplicarVisibilidade();
        this.filtrar();
      },
      error: () => { },
    });
  }

  private aplicarVisibilidade(): void {
    const isMaster = this.authService.getUserInfos()?.master ?? false;

    this.menuGroupsVisiveis = this.menuGroups
      .map(group => ({
        ...group,
        items: group.items.filter(item =>
          (!item.somenteMaster || isMaster)
          && (!item.exigeSeparacao || this.isTrabalhaComSeparacao)
          && (!item.exigeExpedicao || this.isTrabalhaComExpedicao)),
      }))
      .filter(group => group.items.length > 0);
  }

  public filtrar(): void {
    const termo = this.removeAcentos(this.pesquisaControl.value?.toLowerCase() || '');
    if (!termo) {
      this.filteredMenuGroups = [...this.menuGroupsVisiveis];
      return;
    }

    this.filteredMenuGroups = this.menuGroupsVisiveis
      .map(group => {
        const groupMatches = this.removeAcentos(group.title.toLowerCase()).includes(termo);
        const items = groupMatches
          ? group.items
          : group.items.filter(item => this.removeAcentos(item.label.toLowerCase()).includes(termo));

        return { ...group, items };
      })
      .filter(group => group.items.length > 0);
  }

  public get nenhumResultado(): boolean {
    return this.filteredMenuGroups.length === 0;
  }

  private removeAcentos(str: string): string {
    return str.normalize('NFD').replace(/[̀-ͯ]/g, '');
  }
}
